import { RecordContextProvider, useListContext, useTranslate } from "ra-core";
import { Skeleton } from "@/components/ui/skeleton";

import { ListEmptyState, ListErrorState } from "../design/ListStates";
import type { Company } from "../types";
import { CompanyCard } from "./CompanyCard";

const GRID_TEMPLATE_COLUMNS = "repeat(auto-fill, minmax(180px, 1fr))";

/**
 * Card placeholders shown while the company grid request is pending
 * (design-system.md section 6/8: skeleton, never a spinner). Same
 * accessible-name pattern as the shared list skeletons in design/ListStates.
 */
const CompanyCardsSkeleton = () => {
  const translate = useTranslate();
  return (
    <div
      aria-busy="true"
      role="status"
      className="w-full gap-2 grid"
      style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
    >
      <span className="sr-only">{translate("crm.common.loading")}</span>
      {Array.from({ length: 10 }, (_, index) => (
        <Skeleton
          key={index}
          aria-hidden="true"
          className="h-[200px] rounded-[20px]"
        />
      ))}
    </div>
  );
};

export const ImageList = () => {
  const { data, error, isPending, refetch } = useListContext<Company>();
  const translate = useTranslate();

  if (isPending) {
    return <CompanyCardsSkeleton />;
  }

  if (error) {
    return (
      <ListErrorState
        message={translate("crm.common.list_error")}
        onRetry={() => refetch()}
      />
    );
  }

  if (!data?.length) {
    return (
      <ListEmptyState
        title={translate("resources.companies.empty.title", {
          _: "No companies found",
        })}
      />
    );
  }

  return (
    <div
      className="w-full gap-2 grid"
      style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
    >
      {data.map((record) => (
        <RecordContextProvider key={record.id} value={record}>
          <CompanyCard />
        </RecordContextProvider>
      ))}
    </div>
  );
};
